// Table components for UI Component Library
// Created as part of ENG2-1691: Phase 3 - Ticket 3.4

import React from 'react'
import {
  Box,
  Table as MuiTable,
  TableBody,
  TableCell as MuiTableCell,
  TableContainer,
  TableHead,
  TableRow as MuiTableRow,
  Typography,
  IconButton,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronLeft,
  faChevronRight,
  faInbox,
} from '@fortawesome/free-solid-svg-icons'
import { tokens } from '../../../styles/tokens'
import {
  DataTableProps,
  TablePaginationProps,
  TableEmptyProps,
  TableHeaderProps,
  TableRowProps,
  TableCellProps,
} from './table.types'

// Styled table container
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: tokens.radius.lg,
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
}))

// Styled header cell
const StyledHeaderCell = styled(MuiTableCell)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : tokens.colors.grey[50],
  padding: '12px 20px',
  borderBottom: `1px solid ${theme.palette.divider}`,
}))

// Styled body cell
const StyledBodyCell = styled(MuiTableCell)(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.text.primary,
  padding: '14px 20px',
  borderBottom: `1px solid ${theme.palette.divider}`,
}))

// Styled body row
const StyledBodyRow = styled(MuiTableRow, {
  shouldForwardProp: (prop) => !['clickable', 'selected'].includes(prop as string),
})<{ clickable?: boolean; selected?: boolean }>(({ theme, clickable, selected }) => ({
  cursor: clickable ? 'pointer' : 'default',
  transition: `background-color ${tokens.transitions.fast}`,
  backgroundColor: selected ? theme.palette.action.selected : 'transparent',

  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },

  '&:last-child td': {
    borderBottom: 'none',
  },
}))

// Styled pagination container
const PaginationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '14px 24px',
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}))

// Styled page button
const PageButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ theme, active }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  borderRadius: tokens.radius.sm,
  cursor: 'pointer',
  transition: `all ${tokens.transitions.fast}`,
  backgroundColor: active ? theme.palette.primary.main : 'transparent',
  color: active ? theme.palette.primary.contrastText : theme.palette.text.secondary,
  fontSize: 13,
  fontWeight: active ? 600 : 400,

  '&:hover': {
    backgroundColor: active ? theme.palette.primary.dark : theme.palette.action.hover,
  },
}))

// Styled empty state container
const EmptyContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '60px 24px',
  textAlign: 'center',
})

// DataTable component
export const DataTable: React.FC<DataTableProps> = ({ children, sx, className }) => {
  return (
    <StyledTableContainer sx={sx} className={className}>
      <MuiTable>{children}</MuiTable>
    </StyledTableContainer>
  )
}

// TableHeader component
export const TableHeader: React.FC<TableHeaderProps> = ({ children, sx }) => {
  return (
    <TableHead sx={sx}>
      <MuiTableRow>{children}</MuiTableRow>
    </TableHead>
  )
}

// TableHeaderCell component
export const TableHeaderCell: React.FC<TableCellProps> = ({
  children,
  align = 'left',
  width,
  sx,
}) => {
  return (
    <StyledHeaderCell align={align} sx={{ width, ...sx }}>
      {children}
    </StyledHeaderCell>
  )
}

// TableBody is re-exported from MUI
export { TableBody }

// TableRow component
export const TableRow: React.FC<TableRowProps> = ({
  children,
  onClick,
  clickable = false,
  selected = false,
  sx,
}) => {
  return (
    <StyledBodyRow
      clickable={clickable || !!onClick}
      selected={selected}
      onClick={onClick}
      sx={sx}
    >
      {children}
    </StyledBodyRow>
  )
}

// TableCell component
export const TableCell: React.FC<TableCellProps> = ({
  children,
  align = 'left',
  width,
  sx,
}) => {
  return (
    <StyledBodyCell align={align} sx={{ width, ...sx }}>
      {children}
    </StyledBodyCell>
  )
}

// TablePagination component
export const TablePagination: React.FC<TablePaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  itemName = 'items',
  sx,
}) => {
  const startItem = currentPage * pageSize + 1
  const endItem = Math.min((currentPage + 1) * pageSize, totalItems)

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = []
    const maxPagesToShow = 5

    if (totalPages <= maxPagesToShow) {
      // Show all pages
      for (let i = 0; i < totalPages; i++) {
        pages.push(i)
      }
    } else {
      // Show first, last, current, and neighbors with ellipsis
      pages.push(0)

      if (currentPage > 2) {
        pages.push('...')
      }

      const start = Math.max(1, currentPage - 1)
      const end = Math.min(totalPages - 2, currentPage + 1)

      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) {
          pages.push(i)
        }
      }

      if (currentPage < totalPages - 3) {
        pages.push('...')
      }

      if (!pages.includes(totalPages - 1)) {
        pages.push(totalPages - 1)
      }
    }

    return pages
  }

  return (
    <PaginationContainer sx={sx}>
      <Typography
        sx={{
          fontSize: 13,
          color: tokens.colors.grey[500],
        }}
      >
        Showing {startItem} – {endItem} of {totalItems} {itemName}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <IconButton
          size="small"
          disabled={currentPage === 0}
          onClick={() => onPageChange(currentPage - 1)}
          sx={{
            width: 32,
            height: 32,
            color: tokens.colors.grey[500],
            '&:disabled': {
              color: tokens.colors.grey[300],
            },
          }}
        >
          <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: 12 }} />
        </IconButton>

        {getPageNumbers().map((page, index) =>
          page === '...' ? (
            <Box
              key={`ellipsis-${index}`}
              sx={{
                width: 32,
                display: 'flex',
                justifyContent: 'center',
                color: tokens.colors.grey[400],
              }}
            >
              ...
            </Box>
          ) : (
            <PageButton
              key={page}
              active={currentPage === page}
              onClick={() => onPageChange(page as number)}
            >
              {(page as number) + 1}
            </PageButton>
          )
        )}

        <IconButton
          size="small"
          disabled={currentPage === totalPages - 1}
          onClick={() => onPageChange(currentPage + 1)}
          sx={{
            width: 32,
            height: 32,
            color: tokens.colors.grey[500],
            '&:disabled': {
              color: tokens.colors.grey[300],
            },
          }}
        >
          <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: 12 }} />
        </IconButton>
      </Box>
    </PaginationContainer>
  )
}

// TableEmpty component
export const TableEmpty: React.FC<TableEmptyProps> = ({
  icon,
  title,
  description,
  action,
  sx,
}) => {
  return (
    <EmptyContainer sx={sx}>
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          backgroundColor: 'action.hover',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 2,
          color: 'text.disabled',
          fontSize: 24,
        }}
      >
        {icon || <FontAwesomeIcon icon={faInbox} />}
      </Box>
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 600,
          color: 'text.primary',
          marginBottom: 1,
        }}
      >
        {title}
      </Typography>
      {description && (
        <Typography
          sx={{
            fontSize: 14,
            color: 'text.secondary',
            maxWidth: 400,
            marginBottom: action ? 2 : 0,
          }}
        >
          {description}
        </Typography>
      )}
      {action}
    </EmptyContainer>
  )
}

DataTable.displayName = 'DataTable'
TableHeader.displayName = 'TableHeader'
TableHeaderCell.displayName = 'TableHeaderCell'
TableRow.displayName = 'TableRow'
TableCell.displayName = 'TableCell'
TablePagination.displayName = 'TablePagination'
TableEmpty.displayName = 'TableEmpty'

export type {
  DataTableProps,
  TablePaginationProps,
  TableEmptyProps,
  TableHeaderProps,
  TableRowProps,
  TableCellProps,
} from './table.types'
